<template>
    <div class="pagination" v-if="totalPages > 1">
        <button
            @click="goToPage(1)"
            :disabled="currentPage === 1"
            class="pagination-btn"
        >
            &laquo;
        </button>

        <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="pagination-btn"
        >
            &lsaquo;
        </button>

        <template v-for="page in displayedPages" :key="page">
            <button
                v-if="typeof page === 'number'"
                @click="goToPage(page)"
                :class="['pagination-btn', { active: currentPage === page }]"
            >
                {{ page }}
            </button>
            <span v-else class="pagination-ellipsis">...</span>
        </template>

        <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="pagination-btn"
        >
            &rsaquo;
        </button>

        <button
            @click="goToPage(totalPages)"
            :disabled="currentPage === totalPages"
            class="pagination-btn"
        >
            &raquo;
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
    currentPage: number
    totalItems: number
    itemsPerPage: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'page-changed', page: number): void
}>()

const totalPages = computed(() => {
    return Math.ceil(props.totalItems / props.itemsPerPage)
})

const displayedPages = computed(() => {
    const pages: (number | string)[] = []
    const total = totalPages.value
    const current = props.currentPage

    // Always show first page
    pages.push(1)

    // Calculate range around current page
    let start = Math.max(2, current - 2)
    let end = Math.min(total - 1, current + 2)

    // Add ellipsis after first page if needed
    if (start > 2) {
        pages.push('...')
    }

    // Add pages around current
    for (let i = start; i <= end; i++) {
        pages.push(i)
    }

    // Add ellipsis before last page if needed
    if (end < total - 1) {
        pages.push('...')
    }

    // Always show last page if there is more than one page
    if (total > 1) {
        pages.push(total)
    }

    return pages
})

const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
        emit('page-changed', page)
    }
}
</script>

<style scoped>
.pagination {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    padding: 20px 0;
}

.pagination-btn {
    min-width: 40px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #ddd;
    background-color: white;
    color: #333;
    cursor: pointer;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: #999;
}

.pagination-btn.active {
    background-color: #4BB4F5;
    border-color: #4BB4F5;
    color: white;
    font-weight: bold;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;
}

.pagination-ellipsis {
    padding: 0 8px;
    color: #999;
}
</style>
